import React from 'react';
import TimeLine from './TimeLine';
import { Stack, useMediaQuery } from '@mui/material';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CompareIcon from '@mui/icons-material/Compare';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { t } from 'i18next';
import theme from '../../theme';
import useCache, { IMAGE_NDREI_TIFF, IMAGE_NDREI_VINEYARD_TIFF, IMAGE_NDVI_TIFF, IMAGE_NDVI_VINEYARD_TIFF, CONTOUR_LINES_DXF, CONTOUR_LINES_SHP, GLB_FILE, IMAGE_ORTHOPHOTO } from '../../common/hooks/useCache';
import JSZip from 'jszip';
import { NewButton } from '../NewButton';
import { useNavigate } from 'react-router-dom';
import useEmbed from '../../common/hooks/useEmbed';
import { globalAvailableIndexes } from '../../utils/indexes';
import ActionsButton, { ActionItem } from './ActionsButton';

export interface FloatingBottomBarProps {
  analysis: {
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      orthophoto_png: string,
      phase: string,
      index_data: {
        name: string,
        png: string,
        tiff: string,
        average: number,
        min: number,
        max: number,
        std_dev: number,
      }[],
    }[],
  }[];
  currentAnalysis?: {
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      phase: string,
    }[],
  };
  selectedIndex?: string;
  setCurrentAnalysis: (analysis: any) => void;
  zonationAvailable?: boolean;
  measuring?: boolean;
  setMeasuring?: (measuring: boolean) => void;
  hideButtons?: boolean;
}

function FloatingBottomBar(props: FloatingBottomBarProps) {
  const { downloadPresigned } = useCache();

  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const navigate = useNavigate();
  const { appendParams } = useEmbed();

  const downloadMap = async () => {
    if (!props.currentAnalysis) return;

    let orthophoto: Blob | null = null;
    let ndvi: Blob | null = null;
    let ndvi_vineyard: Blob | null = null;
    let ndrei: Blob | null = null;
    let ndrei_vineyard: Blob | null = null;
    let glb: Blob | null = null;
    let contour_lines_dxf: Blob | null = null;
    let contour_lines_shp: Blob | null = null;
    let contour_lines_dbf: Blob | null = null;
    let contour_lines_prj: Blob | null = null;
    let contour_lines_shx: Blob | null = null;

    try {
      orthophoto = await downloadPresigned(props.currentAnalysis.uuid, IMAGE_ORTHOPHOTO);
    } catch (error) {
      console.error(error);
    }
    try {
      ndvi = await downloadPresigned(props.currentAnalysis.uuid, IMAGE_NDVI_TIFF);
    } catch (error) {
      console.error(error);
    }
    try {
      ndvi_vineyard = await downloadPresigned(props.currentAnalysis.uuid, IMAGE_NDVI_VINEYARD_TIFF);
    } catch (error) {
      console.warn(error);
    }
    try {
      ndrei = await downloadPresigned(props.currentAnalysis.uuid, IMAGE_NDREI_TIFF);
    } catch (error) {
      console.error(error);
    }
    try {
      ndrei_vineyard = await downloadPresigned(props.currentAnalysis.uuid, IMAGE_NDREI_VINEYARD_TIFF);
    } catch (error) {
      console.warn(error);
    }
    try {
      glb = await downloadPresigned(props.currentAnalysis.uuid, GLB_FILE);
    } catch (error) {
      console.error(error);
    }
    try {
      contour_lines_dxf = await downloadPresigned(props.currentAnalysis.uuid, CONTOUR_LINES_DXF);
    } catch (error) {
      console.error(error);
    }
    try {
      contour_lines_shp = await downloadPresigned(props.currentAnalysis.uuid, CONTOUR_LINES_SHP);
    } catch (error) {
      console.error(error);
    }
    try {
      contour_lines_dbf = await downloadPresigned(props.currentAnalysis.uuid, `${CONTOUR_LINES_SHP}.dbf`);
    } catch (error) {
      console.error(error);
    }
    try {
      contour_lines_prj = await downloadPresigned(props.currentAnalysis.uuid, `${CONTOUR_LINES_SHP}.prj`);
    } catch (error) {
      console.error(error);
    }
    try {
      contour_lines_shx = await downloadPresigned(props.currentAnalysis.uuid, `${CONTOUR_LINES_SHP}.shx`);
    } catch (error) {
      console.error(error);
    }

    const zip = new JSZip();
    orthophoto && zip.file('orthophoto.tif', orthophoto);
    ndvi && zip.file('ndvi.tif', ndvi);
    ndvi_vineyard && zip.file('ndvi_vineyard.tif', ndvi_vineyard);
    ndrei && zip.file('ndrei.tif', ndrei);
    ndrei_vineyard && zip.file('ndrei_vineyard.tif', ndrei_vineyard);
    glb && zip.file('model.glb', glb);
    contour_lines_dxf && zip.file('contour_lines.dxf', contour_lines_dxf);
    contour_lines_shp && zip.file('contour_lines.shp', contour_lines_shp);
    contour_lines_dbf && zip.file('contour_lines.dbf', contour_lines_dbf);
    contour_lines_prj && zip.file('contour_lines.prj', contour_lines_prj);
    contour_lines_shx && zip.file('contour_lines.shx', contour_lines_shx);

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${props.currentAnalysis.uuid}.zip`;
    a.click();

    URL.revokeObjectURL(url);
  };

  // Create actions array for the ActionsButton
  const actions: ActionItem[] = [
    {
      id: 'download-map',
      label: 'analysis.actions.downloadMap',
      icon: <CloudDownloadIcon />,
      onClick: downloadMap,
      disabled: false
    },
    // Add more actions here as needed
  ];

  return (
    <React.Fragment>
      <Stack 
        direction="row"
        spacing={2}
        sx={{
          position: 'absolute',
          bottom: 25,
          left: 8,
          right: 8,
          pointerEvents: 'none', // Make the container non-interactive
          zIndex: 10000,
        }}
        alignItems={'end'}
        justifyContent={'space-between'}
      >
        <TimeLine
          analysis={props.analysis}
          currentAnalysis={props.currentAnalysis}
          currentIndex={props.selectedIndex}
          onAnalysisSelected={(analysisUuid) => {
            props.setCurrentAnalysis(props.analysis.find((a) => a.uuid === analysisUuid) ?? null);
            if (analysisUuid) {
              const url = new URL(window.location.href);
              url.searchParams.set('analysis', analysisUuid);
              window.history.replaceState({}, document.title, url.toString());
            }
          }}
          contentWidth={isMobile ? 'calc(100% - 90px)' : 'calc(100% - 16px)'}
          contained
        />
        { !props.hideButtons && <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={1}
          sx={{ pointerEvents: 'auto' }} // Re-enable pointer events for buttons
        >
          { props.selectedIndex !== "GLB" &&
            <NewButton
              text={t('analysis.button_measure')}
              icon={<SquareFootIcon />}
              onClick={() => props.setMeasuring?.(!props.measuring)}
              color={props.measuring === true ? 'secondary' : 'white'}
              fixedPosition={false}
              breakpoint='lg'
            />
          }
          { props.currentAnalysis?.uuid && 
            props.selectedIndex &&
            props.selectedIndex !== globalAvailableIndexes[0].id &&
            !globalAvailableIndexes.find((index) => index.id === props.selectedIndex)?.noCompare && (
            <NewButton
              text={t('analysis.button_compare')}
              icon={<CompareIcon />}
              fixedPosition={false}
                breakpoint='lg'
                color='white'
                onClick={() => {
                  navigate(
                    appendParams(`./compare/${props.currentAnalysis?.uuid}/${props.selectedIndex}`)
                  );
                }}
              />
            )
          }
          <ActionsButton
            actions={actions}
            useDropdown
            breakpoint='lg'
            fixedPosition={false}
          />
        </Stack>}
      </Stack>
    </React.Fragment>
    
  );
}

export default FloatingBottomBar;
